import { useAuth } from '../contexts/auth.context';
import { useTenantStore } from '../stores/tenant.store';
import {
  rolesCanAccess,
  ROLE_DEFAULT_ROUTE,
  ROLE_PRIORITY_ORDER,
} from '../config/permissions.config';

// ─── usePermissions hook ──────────────────────────────────────────────────────
// Route-level access control. Determines:
//   - Which nav items are visible (canAccess)
//   - Where to redirect after login (defaultRoute)
//
// ⚠️ THREE STATES, NOT TWO. "I don't know yet" is not "no". Conflating them is
// what made this hook fail OPEN: `currentMembership === null` meant both "still
// loading" and "loaded, this user has no membership", and the loading reading
// won — so during the load window ANY authenticated user passed every route
// guard. Callers must branch on `permissionsReady` BEFORE reading `canAccess`.

export function usePermissions() {
  const { isPlatformAdmin, loading } = useAuth();
  const { currentMembership } = useTenantStore();

  /** True once auth resolution has FINISHED — success or failure.
   *
   *  Deliberately derived from `auth.loading` rather than a second flag in the
   *  tenant store. `AuthProvider` awaits the membership load (including its
   *  catch) and only then calls `setLoading(false)`, so this bit already means
   *  exactly "the attempt is over". A parallel `membershipStatus` field would be
   *  a second thing to keep in sync with the first — the same two-fields-one-
   *  truth shape behind the bugs this PR is closing. */
  const permissionsReady = !loading;

  const roles: string[] = isPlatformAdmin
    ? ['platformAdmin']
    : ((currentMembership?.roles as string[] | undefined) ?? []);

  const isAdmin = isPlatformAdmin || roles.includes('tenantAdmin');

  function hasRole(role: string): boolean {
    return roles.includes(role);
  }

  function hasAnyRole(check: string[]): boolean {
    return check.some((r) => roles.includes(r));
  }

  /**
   * Returns true if ANY of the user's roles permits access to this path.
   *
   * FAIL-CLOSED. While permissions are still resolving this returns `false`,
   * which is NOT the same as "denied" — callers must show a loading state
   * instead of redirecting (see `RoleGuard`). Treating this `false` as a denial
   * during the load window turns the old fail-open bug into a premature
   * fail-closed one.
   */
  function canAccess(path: string): boolean {
    if (!permissionsReady) return false;
    if (isAdmin) return true;
    if (roles.length === 0) return false;
    return rolesCanAccess(roles, path);
  }

  /**
   * The "home" route for this user, based on their highest-priority role.
   * Only meaningful once `permissionsReady` — before that the roles list is
   * empty and this falls through to the last resort.
   */
  function getDefaultRoute(): string {
    for (const role of ROLE_PRIORITY_ORDER) {
      if (roles.includes(role) && ROLE_DEFAULT_ROUTE[role]) {
        return ROLE_DEFAULT_ROUTE[role];
      }
    }
    // Last resort for an authenticated user whose roles grant no home. It is
    // NOT a seminary screen on purpose: landing a role we don't recognise on
    // /admissions is how the TMAI reviewer ended up staring at an empty
    // admissions list inside the network tenant.
    return '/no-access';
  }

  return {
    roles,
    isAdmin,
    hasRole,
    hasAnyRole,
    canAccess,
    permissionsReady,
    defaultRoute: getDefaultRoute(),
  };
}
